import React from 'react';
import { matchPath, useLocation } from 'react-router';
import { DisplayType, SessionFieldsFragment } from '../../../../generated/core-api/graphql-react-query';
import useGlobalStoreContext from '../../../Contexts/GlobalContext/Contexts/GlobalStoreContext/useGlobalStoreContext';
import useDisplay from '../../../Contexts/GlobalContext/Hooks/useDisplay';
import useHistory from '../../../Hooks/useHistory/useHistory';
import { useOrganisationData } from '../../../Hooks/useOrganisation';
import useContentMaps from '../../Hooks/useSession/useContentMaps';
import useSessionStoreContext from '../Contexts/SessionStoreContextProvider/useSessionStoreContext';
import useDetectionSnackbar from './useDetectionSnackbar';
import useSessionInfo from './useSessionInfo';

function useDetectionNavigation() {
  const { latestDetection: [latestDetection], focusMode: [focusMode] } = useSessionStoreContext();
  const { projectId: [projectId] } = useGlobalStoreContext();
  const location = useLocation();
  const isDecisionTreeRoute = React.useMemo(() => !!matchPath('/decision-trees/*', location.pathname), [location.pathname]);
  const projectIdRef = React.useRef<string | null>(projectId);
  const { cardTitleMap, commandTitleMap } = useContentMaps(latestDetection?.projectId);

  React.useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  const focusModeRef = React.useRef(focusMode);
  const detectionIdRef = React.useRef<string | null>(null);
  const lastHandledDetectionIdRef = React.useRef<string | null>(null);
  const display = useDisplay();
  const openDetectionSnackbar = useDetectionSnackbar(display);

  React.useEffect(() => {
    if (latestDetection && latestDetection.cardId) {
      detectionIdRef.current = latestDetection.id;
    }
  }, [latestDetection]);

  React.useEffect(() => {
    focusModeRef.current = focusMode;
  }, [focusMode]);

  React.useEffect(() => {
    if (latestDetection?.cardId && !focusModeRef.current) {
      if (!projectIdRef.current || !detectionIdRef.current) {
        console.warn('Project ID or Detection ID is not set, cannot navigate');
      } else if (lastHandledDetectionIdRef.current !== detectionIdRef.current) {
        lastHandledDetectionIdRef.current = detectionIdRef.current;
        if (location.pathname.endsWith('/search') || isDecisionTreeRoute) {
          openDetectionSnackbar({
            cardId: latestDetection.cardId,
            cardTitle: cardTitleMap.get(latestDetection.cardId),
            commandTitle: commandTitleMap.get(latestDetection.commandId),
            tabId: latestDetection.tabId,
          });
        } else {
          display(latestDetection.cardId, DisplayType.Detection, latestDetection.tabId);
        }
      }
    }
  }, [cardTitleMap, commandTitleMap, display, isDecisionTreeRoute, latestDetection, location.pathname, openDetectionSnackbar]);
}

function useSessionUpdateNavigation() {
  const session = useSessionInfo();
  const sessionRef = React.useRef<SessionFieldsFragment | null>(null);
  const org = useOrganisationData();
  const { navigate } = useHistory();
  const display = useDisplay();

  React.useEffect(() => {
    if (session?.id !== sessionRef.current?.id) {
      if (session?.id) {
        // New session
        if (session.externalCall?.clientCard?.id) {
          // If external client card
          display(session.externalCall?.clientCard?.id, DisplayType.InitialDisplay);
        } else if (session.project.sessionStartCardId) {
          // If session start card
          display(session.project.sessionStartCardId, DisplayType.SessionStartDisplay);
        } else if (!org.features.cogneedAi?.liveSessionDisableLiveSessionPageNavigation?.enabled) {
          // If no session start card and feature is enabled, navigate to live session page
          navigate('/live-session');
        }
      } else
        // Old session
        if (sessionRef.current.project.sessionEndCardId) {
          // Old session had session end card
          display(sessionRef.current.project.sessionEndCardId, DisplayType.SessionEndDisplay);
        }
    }

    sessionRef.current = session;
  }, [session]);
}

export default function useSessionNavigation() {
  useDetectionNavigation();
  useSessionUpdateNavigation();
}
